import { Avatar, Box, Card, CardActions, CardContent, CardMedia, List, Typography } from '@mui/material';
import dayjs from 'dayjs';
import parse from 'html-react-parser';
import { useSelector } from 'react-redux';
import { RootStateType } from 'src/services/redux/reducers';
import { useGlobals } from 'src/services/contexts/appContext';
import ModalTemplate from 'src/shared/templates/modalTemplate';
import NewsCommentInput from 'src/modules/home/components/newsCommentInput';
import CommentsList from 'src/modules/home/components/commentsList';
import ParticipantsButton from 'src/modules/home/components/participantsButton';
import { useTranslation } from 'react-i18next';
import ActivityButtons from 'src/modules/home/components/activityButtons';
import ViewFile from 'src/modules/home/components/viewFile';
import { Colors } from 'src/shared/components/styles/colors';
import NewsActionLikeComment from 'src/modules/home/components/newsActionLikeComment';
import ReadConfirmButton from 'src/modules/home/components/readConfirmButton';
import { useFetchNewsById } from 'src/modules/home/hooks/queries/useFetchNewsById';
import { useMarkAsReadAndUnderstood } from 'src/modules/home/hooks/mutations/useMarkAsReadAndUnderstood';
import { UserTypeEnum } from 'src/shared/enums/userType.enum';
import { NewsCategoryEnum } from 'src/shared/enums/newsCategory.enum';
import LockClockIcon from '@mui/icons-material/LockClock';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import activityGif from '../../../assets/images/calendar.gif';
import newsPaperGif from '../../../assets/images/newspaper.gif';
import { htmlParserOptions } from './newsListItem';

type NewsModalProps = {
  newsGUID: string;
  modalOpen: boolean;
  setModalOpen: (arg: boolean) => void;
};

export const NewsModal = (props: NewsModalProps) => {
  const { modalOpen, setModalOpen, newsGUID } = props;
  const { t } = useTranslation(['news']);

  const { user } = useSelector((state: RootStateType) => state.oidc);
  const { domain } = useSelector((state: RootStateType) => state.appState);

  const { loggedInUser } = useGlobals();
  const isAdmin = loggedInUser?.UserType === UserTypeEnum.Admin;

  // useQuery
  const { isLoading, data: newsItem } = useFetchNewsById(newsGUID);

  // useMutation
  const markAsConfirmed = useMarkAsReadAndUnderstood();

  const markAsConfirmedHandler = async () => {
    markAsConfirmed.mutate(newsGUID);
  };

  return (
    <ModalTemplate
      isOpen={modalOpen}
      onClose={() => setModalOpen(false)}
      isLoading={isLoading}
      title=''
      subtitle={
        <Box ml={2} mt={1}>
          {newsItem?.Creator && newsItem.Creator.GUID && (
            <Box display='flex' alignItems='center' gap={2}>
              <Avatar
                src={
                  newsItem?.PublishAsOrganisation
                    ? `https://${domain}.easystaff.se${newsItem?.CompanyLogotype}?access_token=${user?.access_token}`
                    : `${newsItem?.Creator?.Image}?access_token=${user?.access_token}`
                }
                sx={{ width: 48, height: 48 }}
              />
              <Box>
                <Typography variant='body2' fontWeight='bold'>
                  {newsItem?.PublishAsOrganisation
                    ? newsItem?.CompanyName
                    : `${newsItem?.Creator?.Firstname ?? '-'} ${newsItem?.Creator?.Lastname ?? '-'}`}
                </Typography>

                {isAdmin && newsItem?.PublishAsOrganisation && (
                  <Typography sx={{ fontSize: '13px', fontWeight: 400 }}>
                    {t('news:publishedBy')} {newsItem?.Creator?.Firstname} {newsItem?.Creator?.Lastname}
                  </Typography>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: Colors.GRAY_DARK,
                    fontSize: '13px',
                    mt: 0.3,
                  }}
                >
                  <Typography>{dayjs(newsItem?.PublishedDate).format('D MMM YYYY')}</Typography>
                  <Typography>•</Typography>
                  <img width='14px' src={newsItem?.Category === 'Aktiviteter' ? activityGif : newsPaperGif} alt='category' />
                  <Typography>{newsItem?.Category}</Typography>
                </Box>
              </Box>
            </Box>
          )}

          {newsItem?.Heading && (
            <Typography mt={2} variant='h2'>
              {newsItem.Heading}
            </Typography>
          )}
        </Box>
      }
      content={
        <>
          <Card
            sx={{
              maxWidth: '100%',
              boxShadow: 'none !important', // Overrida cards boxshadow
              display: 'flex',
              flexDirection: 'column',
              borderEndStartRadius: 0,
            }}
          >
            {newsItem?.Image && (
              <CardMedia
                sx={{
                  display: 'flex',
                  alignSelf: 'center',
                  justifyContent: 'center',
                  maxHeight: 'auto',
                  maxWidth: '100%',
                  px: 1,
                  '&.MuiCardMedia-img': {
                    width: 'auto',
                    objectFit: 'contain',
                  },
                }}
                image={`${newsItem?.Image}?access_token=${user?.access_token}`}
                component='img'
              />
            )}
            <CardContent>
              <Typography variant='h5' sx={{ fontSize: '20px' }}>
                {newsItem?.Teaser}
              </Typography>
              <Typography variant='body2' component='div' sx={{ fontSize: '16px' }}>
                {parse(newsItem?.BodyText ?? '', htmlParserOptions(user, domain))}
              </Typography>
              <List sx={{ display: 'flex', flexDirection: 'column', px: 0, mt: 2 }}>
                {newsItem?.PDF && <ViewFile doc={newsItem?.PDF} />}
                {newsItem?.PDFs && newsItem?.PDFs.map((doc) => <ViewFile key={doc} doc={doc} />)}
              </List>
            </CardContent>
            {newsItem?.ShowConfirmAction && <ReadConfirmButton readStatus={newsItem?.ConfirmedByMe} onClick={() => markAsConfirmedHandler()} />}
            {newsItem?.Category === NewsCategoryEnum.Activities && (
              <>
                {newsItem?.ActivityDate && (
                  <Box display='flex' alignItems='center' flexDirection='row' px={1} mb={1}>
                    <CalendarMonthIcon />
                    <Typography variant='h6' pl={0.5}>
                      {t('news:activityDate')} {dayjs(newsItem?.ActivityDate).format('dddd')} {dayjs(newsItem?.ActivityDate).format('DD MMM YYYY')} kl{' '}
                      {dayjs(newsItem?.ActivityDate).format('HH:mm')}
                    </Typography>
                  </Box>
                )}
                {newsItem?.DeadlineDate && (
                  <Box display='flex' alignItems='center' flexDirection='row' px={1} mb={1}>
                    <LockClockIcon />
                    <Typography variant='h6' pl={0.5}>
                      {t('news:deadlineDate')} {dayjs(newsItem?.DeadlineDate).format('YYYY-MM-DD')}
                    </Typography>
                  </Box>
                )}
              </>
            )}
            <CardActions
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Box>{newsItem?.Category === NewsCategoryEnum.Activities && <ActivityButtons newsItem={newsItem} />}</Box>
              <Box>{newsItem && newsItem.Participants.length > 0 && <ParticipantsButton items={newsItem.Participants} />}</Box>
            </CardActions>
          </Card>
          {newsItem && (
            <Box px={1} py={1}>
              <NewsActionLikeComment item={newsItem} />
            </Box>
          )}
          {newsItem?.CanBeCommented && (
            <Box px={1}>
              <NewsCommentInput newsItem={newsItem} />
              <CommentsList items={newsItem} />
            </Box>
          )}
        </>
      }
    />
  );
};
